import React, {useCallback, useMemo} from 'react';
import {CardImage} from './ImageWithOverlays';
import {ControlsImageOverlays} from './ControlImageOverlays';
import * as MediaImage from './mediaImage';
import * as Controls from './controlsImage';
import * as Panel2 from './panel';
import * as CGReact from './cg/cgreact';
import * as Op from './cg/ops';

const inputType = 'image-file' as const;

export interface PanelImageConfig {
  overlayControls?: Controls.OverlayControls;
}
type PanelImageProps = Panel2.PanelProps<typeof inputType, PanelImageConfig>;

const PanelImageConfig: React.FC<PanelImageProps> = props => {
  const {context, config, updateConfig} = props;

  return (
    <ControlsImageOverlays
      maskControls={config.overlayControls}
      classSets={context.classSets}
      setMaskControls={(controlId, newControl) =>
        updateConfig({
          overlayControls: {
            ...config.overlayControls,
            [controlId]: newControl,
          },
        })
      }
    />
  );
};

const PanelImage: React.FC<PanelImageProps> = props => {
  const {context, updateContext, config, updateConfig} = props;
  const inputNode = props.input.path;
  const nodeValueQuery = CGReact.useNodeValue(inputNode);
  const imageArtifact = useMemo(() => Op.opImageArtifact({asset: inputNode}), [
    inputNode,
  ]);

  const image: MediaImage.WBImage = nodeValueQuery.result;

  const overlayControls = useMemo(() => config.overlayControls ?? {}, [
    config.overlayControls,
  ]);
  const classSets = context.classSets;

  const setMaskControls = useCallback(
    (controlId: string, control: Controls.OverlayState) => {
      updateConfig({
        overlayControls: {...overlayControls, [controlId]: control},
      });
    },
    [overlayControls, updateConfig]
  );
  const setClassSet = useCallback(
    (classSetId: string, classSet: Controls.ClassSetState) => {
      updateContext({
        classSets: {...classSets, [classSetId]: classSet},
      });
    },
    [classSets, updateContext]
  );
  const loadedImages = useMemo(() => {
    return [
      {
        loadedFrom: imageArtifact,
        image,
      },
    ];
  }, [image, imageArtifact]);
  const {maskControlsIDs, boxControlsIDs} = Controls.useWBImageControls(
    loadedImages,
    overlayControls,
    classSets,
    setMaskControls,
    setClassSet,
    nodeValueQuery.loading
  );

  return nodeValueQuery.loading ? (
    <div></div>
  ) : image == null ? (
    <div>-</div>
  ) : (
    <CardImage
      image={{
        path: image.path,
        loadedFrom: imageArtifact,
        width: image.width,
        height: image.height,
      }}
      boundingBoxes={
        image.boxes != null ? Object.values(image.boxes) : undefined
      }
      masks={
        image.masks != null
          ? Object.values(image.masks).map(m => ({
              loadedFrom: imageArtifact,
              path: m.path,
            }))
          : undefined
      }
      classSets={classSets}
      maskControls={maskControlsIDs[0].map(id => overlayControls[id]) as any}
      boxControls={boxControlsIDs[0].map(id => overlayControls[id]) as any}
    />
  );
};

export const Spec: Panel2.PanelSpec = {
  id: 'image',
  ConfigComponent: PanelImageConfig,
  Component: PanelImage,
  inputType,
  canFullscreen: true,
  defaultFixedSize: {
    width: 200,
    height: (9 / 16) * 200,
  },
};

// export const MultiImageSpec = makeMultiContainer(Spec);
